// Common functionality for CollectData and SuperviseSession
import ZoomVideo from '@zoom/videosdk';
import { callFirebaseFunction } from 'boot/firebase';
import FileAttachment from 'components/FileAttachment';
import CopyLinkDialog from 'components/Modals/CopyLinkDialog';
import EVVDialog from 'components/Modals/EVVDialog';
import BehaviourProgramData from 'components/Programs/DataCollection/BehaviourProgramData';
import FrequencyTimer from 'components/Programs/DataCollection/FrequencyTimer';
import ProgramInfo from 'components/Programs/ProgramInfo';
import ProgramDataCollection from 'components/Programs/ProgramTypes/ProgramDataCollection';
import SessionActivityNotesEditor from 'components/SessionActivities/SessionActivityNotesEditor';
import AddSessionDialog from 'components/Sessions/Modals/AddSessionDialog';
import SessionProgress from 'components/Sessions/SessionProgress';
import UploadProgress from 'components/UploadProgress';
import MobileVideoContainer from 'components/VideoChat/MobileVideoContainer';
import InviteUser from 'components/VideoChat/Modals/InviteUser';
import VideoContainer from 'components/VideoChat/VideoContainer';
import { arrayRemove, arrayUnion, Timestamp } from 'firebase/firestore';
import {
  chain,
  filter,
  find,
  get,
  head,
  isEmpty,
  isFunction,
  isObject,
  last,
  reduce,
  reject,
  size,
  some,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import { IS_INVITATION_ACCEPTED } from 'src/constants/videoSession';
import DataDB from 'src/fb/data-db';
import DataNotesDB from 'src/fb/dataNotes-db';
import GenericDb from 'src/fb/generic-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionFilesDB from 'src/fb/sessionFiles-db';
import SessionGroupsDB from 'src/fb/sessionGroups-db';
import SessionsDB from 'src/fb/sessions-db';
import Activities from 'src/mixins/Activities';
import Interactions from 'src/mixins/Interactions';
import SessionsMixin from 'src/mixins/SessionsMixin';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { geolocation as geolocationStore } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { programsTargets } from 'src/store/programsTargets';
import { promptLevels } from 'src/store/promptLevels';
import { sessions } from 'src/store/sessions';
import { workflows } from 'src/store/workflows';
import AppUtils from 'src/utils/AppUtils';

export default {

  components: {
    SessionActivityNotesEditor,
    BehaviourProgramData,
    FileAttachment,
    FrequencyTimer,
    MobileVideoContainer,
    ProgramDataCollection,
    SessionProgress,
    UploadProgress,
    VideoContainer,
  },

  mixins: [
    Activities,
    Interactions,
    SessionsMixin,
    Targets,
    Toolbox,
  ],

  data() {
    return {
      /** @type {{ [sessionProgramId: string]: number }} */
      currentTrial: {},
      isLoading: true,
      isAddingGroupParticipant: false,
      preventSessionStart: false,
      unsavedChanges: false,
      expandedView: false,
      endedBySelf: false,

      sessionActivities: [],
      activeSessionProgram: null,

      sessionDuration: 0,

      data: [],
      dataNotes: [],
      sortedTargets: {},

      sessionFiles: [],

      filteredTags: [],

      isVideoCallCreated: false,
      hideSessionComponent: false,
      isShowReturnToVideoCallBtn: false,
      isShowVideoCallBtnLoader: false,
      isVideoMinimized: false,
      isVideoCallCreating: false,
    };
  },

  computed: {
    ...mapState(sessions, [
      'activeSessionGroup',
      'videoSessionMembersNames',
      'activeSessionGroup',
      'groupSessions',
      'activeSession',
      'activeSessionPrograms',
      'isInvitedUser',
      'currentVideoCallSession',
    ]),
    ...mapState(auth, ['userDetails', 'currentClient', 'connectedClinic', 'evvRequired']),
    ...mapState(programs, ['programs']),
    ...mapState(workflows, ['workflows', 'workflowStatuses']),
    ...mapState(network, ['switchingModes']),
    ...mapState(clients, ['clients', 'clientAppointmentsInProgress']),
    ...mapState(promptLevels, ['activePromptLevels']),

    isSessionTimerPaused() {
      return get(this.activeSession, 'durationIsPaused', false);
    }, // isSessionTimerPaused()

    isStaffTrainingSession() {
      return get(this.activeSession, 'sessionType') === 'training';
    }, // isStaffTrainingSession()

    hideButtonLabels() {
      return this.$q.platform.is.mobile || this.expandedView;
    }, // hideButtonLabels()

    visibleSessionPrograms() {
      const variableStatus = this.activeSession.sessionType === 'training' ? ['closed'] : [];
      return this.activeSessionPrograms
        .filter((program) => {
          return !program.hidden
            && [...ACTIVE_PROGRAM_STATUSES, ...variableStatus].includes(program?.program?.status);
        })
        .sort((program, nextProgram) => program.order - nextProgram.order);
    },

    isFiltering() {
      return !isEmpty(this.filteredTags);
    },

    pinnedSessionPrograms() {
      return filter(this.visibleSessionPrograms, { pinned: true });
    }, // pinnedSessionPrograms()

    availableSessionPrograms() {
      return reject(this.visibleSessionPrograms, { pinned: true });
    }, // availableSessionPrograms()

    hasMultipleSessionPrograms() {
      return size(this.availableSessionPrograms) > 1;
    }, // hasMultipleSessionPrograms()

    sessionProgramsData() {
      return chain(this.data)
        .reject({ dataType: 'Behaviour1.0' })
        .keyBy('sessionProgram')
        .value();
    },

    sessionClient() {
      return find(this.clients, { id: this.activeSession?.client });
    }, // sessionClient()

    sessionStaffMember() {
      return chain(this.clients)
        .filter({ type: 'staff' })
        .filter({ status: 'active' })
        .find({ user: this.activeSession?.runBy })
        .value() || null;
    }, // sessionStaffMember()

    currentSessionProgram() {
      const emptyResult = {
        program: {},
      };
      if (!this.activeSession || !this.availableSessionPrograms) {
        return emptyResult;
      }

      const currentSessionProgram = this.availableSessionPrograms.find(
        (sessionProgram) => sessionProgram.id === this.activeSessionProgram,
      );

      return currentSessionProgram || emptyResult;
    },

    currentProgram() {
      return this.currentSessionProgram?.program || null;
    }, // currentProgram()

    previousSessionProgram() {
      if (this.isFirstProgram) {
        return this.currentSessionProgram;
      }
      const currentProgramIndex = this.availableSessionPrograms.indexOf(this.currentSessionProgram) + 1;
      return get(this.availableSessionPrograms, currentProgramIndex - 2, this.currentSessionProgram);
    }, // previousSessionProgram()

    nextSessionProgram() {
      if (this.isLastProgram) {
        return this.currentSessionProgram;
      }
      const currentProgramIndex = this.availableSessionPrograms.indexOf(this.currentSessionProgram) + 1;
      return get(this.availableSessionPrograms, currentProgramIndex, this.currentSessionProgram);
    }, // nextSessionProgram()

    photosUploaded() {
      // We categorize everything that's not a video as a photo to
      // prevent the user from thinking that a files has not been uploaded
      // if it's a non-standard type
      return size(this.sessionFiles) - this.videosUploaded;
    }, // photosUploaded()

    videosUploaded() {
      return reduce(this.sessionFiles, (pV, file) => {
        const type = get(file, 'type', '');

        // after video transcoding, the type changes to application/x-mpegURL
        if (type.startsWith('video') || type === 'application/x-mpegURL') {
          return pV + 1;
        }

        return pV;
      }, 0);
    }, // videosUploaded()

    isFirstProgram() {
      return (this.currentSessionProgram?.id === get(head(this.availableSessionPrograms), 'id', ''));
    }, // getNextLabel()

    isLastProgram() {
      return (this.currentSessionProgram?.id === get(last(this.availableSessionPrograms), 'id', ''));
    }, // getNextLabel()

    isNativeIos() {
      return this.$q.platform.is.capacitor && this.$q.platform.is.ios;
    }, // isNativeIos()

    isNativeAndroid() {
      return this.$q.platform.is.capacitor && this.$q.platform.is.android;
    }, // isNativeAndroid()

    joinVideoLink() {
      return `${AppUtils.getCurrentOrigin()}/#${this.$route.path}?client=${this.activeSession.client}`;
    },

    isSessionInactive() {
      if (!this.activeSession) {
        // Inactive because no active session was loaded
        return true;
      }
      if (get(this.activeSession, 'supervisedBy') === this.userDetails.userId) {
        // Session is being supervised by current user
        return false;
      }
      return (['offline-done', 'done', 'processing'].includes(get(this.activeSession, 'status')));
    }, // isSessionInactive()

    sessionInactiveMessage() {
      if (this.isSessionInactive) {
        return this.$t('run.collectData.sessionNotActiveText');
      }
      return this.$t('run.collectData.loadingData');
    }, // sessionInactiveMessage()

    sessionNoteTemplate() {
      return get(this.activeSession, 'sessionNoteTemplate');
    }, // sessionNoteTemplate()

    sessionDurationOffset() {
      if (chain(this.activeSession).get(['durationOffsetTimestamp', 'toDate']).isFunction().value()) {
        return this.activeSession.durationOffsetTimestamp.toDate();
      }
      return null;
    }, // sessionDurationOffset()

    isActiveSessionProgress() {
      return !this.isVideoCallCreated || (this.$q.platform.is.mobile && this.isVideoMinimized);
    }, // isActiveSessionProgress()

    /**
     * Check if the session program workflow exists
     *
     * @returns {Array<Object>} - programs that have invalid workflow
     */
    filterInvalidWorkflowPrograms() {
      return filter(this.activeSessionPrograms, (sessionProgram) => {
        const programWorkflow = get(sessionProgram, ['program', 'workflow']);
        return (!!programWorkflow
          && !find(this.workflows, { id: programWorkflow }));
      });
    }, // filterInvalidWorkflowPrograms()

  },

  watch: {

    'activeSessionGroup.groupParticipants': {
      immediate: false,
      deep: true,
      async handler() {
        if (this.isLoading || this.isAddingGroupParticipant) {
          return;
        }
        this.isAddingGroupParticipant = true;
        try {
          await this.bindGroupSessions();
        } catch (err) {
          this.notifyNegative(err.message);
        } finally {
          this.isAddingGroupParticipant = false;
        }
      },
    },

    isInvitedUser: {
      immediate: true,
      deep: false,
      async handler() {
        if (this.isLoading) {
          return;
        }
        await this.handleVideoCallInvitation();
      },
    },

    currentVideoCallSession: {
      immediate: true,
      deep: false,
      async handler(newVal, oldVal) {
        if (isEmpty(newVal) && !isEmpty(oldVal)) {
          await this.closeVideoChat();
        }

        if (isEmpty(newVal)) {
          this.isShowVideoCallBtnLoader = false;
        }
      },
    },

    switchingModes(value) {
      if (value) {
        window.removeEventListener('beforeunload', this.beforeWindowUnload);
      }
    },
  },

  async created() {
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  }, // created()

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  }, // beforeUnmount()

  methods: {
    ...mapActions(sessions, [
      'loadActiveSession',
      'bindGroupSessions',
      'setActiveSessionProgramsTargetStatusSelected',
      'updateTargetProperties',
      'loadVideoSessionMembersNames',
      'setVideoSessionMembersNames',
      'clearGroupSessionPrograms',
      'getActiveVideoSession',
    ]),
    ...mapActions(auth, ['syncCurrentClient', 'canUserCreateVideoSession']),
    ...mapActions(programsTargets, ['invalidateProgramTargetsCache']),
    ...mapActions(promptLevels, ['loadPromptLevelTemplates']),
    ...mapActions(geolocationStore, ['refreshGeolocation', 'executeGeolocationCallback']),
    ...mapActions(workflows, ['reloadWorkflows', 'resetWorkflowCacheLastFetch']),

    confirmLeave() {
      // eslint-disable-next-line no-alert
      return window.confirm(this.$t('run.collectData.confirmLeave'));
    },

    confirmStayOnPage() {
      return this.unsavedChanges && !this.confirmLeave();
    },

    beforeWindowUnload(e) {
      // we cannot use custom alerts or custom message here

      // Cancel the event
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = '';
    },

    /**
     * Initialize data for e.g. frequency programs where we want to record
     * "no observations"
     */
    initEmptyTargetData(sessionProgram) {
      const sessionProgramSelectedTargets = get(
        sessionProgram,
        'selectedTargets',
        false,
      );

      return chain(sessionProgram)
        .get(['program', 'targets'])
        // Reject statuses that are not used in session.
        // If no targets were found it indicates that the program has custom status, so we should always include it
        // as custom statuses cannot be hidden
        .filter(
          (target) => {
            return sessionProgramSelectedTargets
              ? get(sessionProgramSelectedTargets, target.status, true)
              : false;
          },
        )
        .map((target) => ({
          target: target.id,
        }))
        .value();
    }, // initEmptyTargetData()

    onShowProgramInfo(program, defaultTab = null) {
      const client = find(this.clients, { id: program.client }) || null;
      this.openComponentDialog(
        ProgramInfo,
        {
          program,
          defaultTab,
          client,
        },
        (async (targetsUpdated) => {
          if (!this) {
            // Module already destroyed
            return;
          }

          if (targetsUpdated && this.activeSession.id) {
            await this.loadActiveSession({
              sessionId: this.activeSession.id,
              forceUpdate: true,
            });
            this.refreshAllSortedTargets();
            // We should clear 'groupSessionPrograms' after updating target to trigger dispatching
            // 'loadSessionPrograms' while switching client tabs in the group session
            await this.clearGroupSessionPrograms({ sessionId: this.activeSession.id });
          }
        }),
      );
    }, // onShowProgramInfo()

    /**
     * Add a participant to a session group.
     * @param {string} sessionGroupId The session group ID
     * @param {Object} groupParticipant The participant to add
     * @returns {Promise<void>}
     */
    async addParticipant(sessionGroupId, groupParticipant) {
      if (!groupParticipant) {
        return;
      }
      this.isAddingGroupParticipant = true;
      try {
        const newSessionId = await this.prepareSession(groupParticipant.originalSession);
        if (!newSessionId) {
          this.notifyNegative(this.$t('run.prepareGroupSession.notifySessionNotPrepared', {
            participantName: groupParticipant.userName ?? '',
          }));
          return;
        }

        const groupParticipants = arrayUnion(
          {
            client: groupParticipant.client,
            session: newSessionId,
            order: chain(this.activeSessionGroup)
              .get('groupParticipants')
              .maxBy('order')
              .get('order', 0)
              .add(1)
              .value(),
          },
        );
        const appointmentId = groupParticipant.appointment;

        const sessionGroupsDb = new SessionGroupsDB();
        const sessionsDb = new SessionsDB();
        const genericDb = new GenericDb();

        // should use backend function in case if offline mode is not used
        if (!this.offlineMode) {
          await sessionGroupsDb.set({ groupParticipants }, sessionGroupId);
          const startSessionOptions = {
            sessionId: newSessionId, appointmentId, sessionGroupId, userId: groupParticipant.user,
          };
          await this.startRunningSessionViaBackend(startSessionOptions);
        } else {
          await genericDb.executeTransaction(async (transactionMethods) => {
            const session = await transactionMethods.read(sessionsDb, { id: newSessionId });
            await transactionMethods.set(sessionGroupsDb, { data: { groupParticipants }, id: sessionGroupId });

            if (!session) {
              return;
            }

            await this.startRunningSession(
              session,
              groupParticipant.user,
              groupParticipant.userName,
              null,
              appointmentId,
              sessionGroupId,
              transactionMethods,
            );
          });
        }

        await this.bindGroupSessions();
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.isAddingGroupParticipant = false;
      }
    }, // addParticipant()

    onAddGroupParticipantFromAppointment(appointment) {
      const sessionGroupId = this.activeSessionGroup?.id;
      if (!sessionGroupId) {
        return;
      }
      const groupParticipant = {
        client: appointment.client,
        originalSession: appointment.session,
        user: appointment.user,
        userName: appointment.userName,
        appointment: appointment.id,
      };

      this.addParticipant(sessionGroupId, groupParticipant);
    }, // onAddGroupParticipantFromAppointment()

    onAddGroupParticipant() {
      const sessionGroupId = this.activeSessionGroup?.id;
      if (!sessionGroupId) {
        return;
      }

      this.openComponentDialog(
        AddSessionDialog,
        {
          title: this.$t('sessions.modals.addGroupParticipant.textTitle'),
        },
        async (groupParticipant) => {
          this.addParticipant(sessionGroupId, groupParticipant);
        },
      );
    }, // onAddGroupParticipant()

    onGotoSessionProgram(sessionProgram) {
      if (!sessionProgram) {
        return;
      }
      this.gotoSessionProgram(sessionProgram.id);
      this.notifyInvalidTargetWorkflow(sessionProgram.program);
      this.notifyInvalidActiveProgramWorkflow(sessionProgram.id);
    }, // onGotoSessionProgram()

    async onNavigate(direction) {
      await this.loadActiveSession({
        sessionId: this.activeSession.id,
        forceUpdate: true,
      });
      this.refreshAllSortedTargets();
      switch (direction) {
        case 'back':
          this.gotoSessionProgram(this.previousSessionProgram.id);
          break;
        case 'forward':
        default:
          this.gotoSessionProgram(this.nextSessionProgram.id);
          break;
      }
    }, // onNavigate()

    async onRefreshTargets(sessionProgram) {
      this.invalidateProgramTargetsCache({ program: sessionProgram.program });
      await this.loadActiveSession({ sessionId: this.activeSession.id, forceUpdate: true });
      this.refreshAllSortedTargets();
    },

    onChangeTargetStatusSelected(targetStatusInfo) {
      this.setActiveSessionProgramsTargetStatusSelected(targetStatusInfo);
      const sessionProgram = get(targetStatusInfo, 'sessionProgram');
      if (!isEmpty(sessionProgram)) {
        this.sortedTargets[sessionProgram?.id] = this.refreshSortedTargets(sessionProgram);
      }
    }, // onChangeTargetStatusSelected()

    async onChangeTargetStatus(sessionProgram, target, newStatus, callback = null) {
      const updateActions = [];

      const targetProperties = {
        status: newStatus,
      };

      // Set mastery date if target is mastered
      if ((newStatus === 'mastered')
        && (target.masteryDate === null)) {
        targetProperties.masteryDate = Timestamp.now();
      }

      // Update session target changes
      const sessionId = get(this.activeSession, 'id', null);
      if (sessionId) {
        const sessionsDb = new SessionsDB();

        const targetChange = chain(this.activeSession)
          .get('targetStatusChanges', [])
          .find({ target: target.id })
          .value();
        if (targetChange) {
          const targetStatusChanges = arrayRemove(targetChange);
          updateActions.push(sessionsDb.set({ targetStatusChanges }, sessionId));
        }

        const targetStatusChanges = arrayUnion({
          program: sessionProgram.program.id,
          target: target.id,
          fromStatus: target.status,
          toStatus: newStatus,
        });
        updateActions.push(sessionsDb.set({ targetStatusChanges }, sessionId));
      }

      // Update target status
      updateActions.push(
        this.updateTargetProperties({
          sessionProgram,
          target,
          targetProperties,
        }),
      );
      await Promise.all(updateActions);
      await this.updateTargetsPerStatus(sessionProgram.program);
      this.refreshAllSortedTargets();
      if (isFunction(callback)) {
        callback();
      }
    }, // onChangeTargetStatus()

    introduceTarget(sessionProgram, target) {
      if (target.introductionDate) {
        return;
      }
      const targetProperties = { introductionDate: Timestamp.now() };
      this.updateTargetProperties({
        sessionProgram,
        target,
        targetProperties,
      });

      const parentTarget = chain(sessionProgram)
        .get(['program', 'targets'])
        .find({ id: target.parentTarget })
        .value();
      if (parentTarget) {
        this.introduceTarget(sessionProgram, parentTarget);
      }
    }, // introduceTarget()

    hasInstructions(sessionProgram) {
      return (!['training'].includes(get(this.activeSession, 'sessionType')))
        && !['asr'].includes(get(sessionProgram, ['program', 'type']));
    }, // hasInstructions()

    hasHistory(sessionProgram) {
      return (!['training'].includes(get(this.activeSession, 'sessionType')))
        && !['asr'].includes(get(sessionProgram, ['program', 'type']));
    }, // hasHistory()

    async finishVideoSession(force = false) {
      if (this.$refs.videoContainer) {
        await this.$refs.videoContainer.leaveCall('remote', force);
      }

      if (this.$refs.mobileVideoContainer) {
        await this.$refs.mobileVideoContainer.leaveCall('remote', force);
      }
    }, // finishVideoSession()

    async canUserParticipateVideoSession() {
      const numberOfParticipants = await this.getNumberOfParticipants();
      return numberOfParticipants < 2;
    }, // canUserParticipateVideoSession()

    async getNumberOfParticipants() {
      try {
        const { data: numberOfParticipants } = await callFirebaseFunction(
          'getNumberOfVideoSessionParticipants',
          { sessionId: get(this.activeSession, 'id') },
        );

        return numberOfParticipants;
      } catch (err) {
        console.error(`Error checking current user is allowed for the video session: ${err}`);
      }
    },

    async establishVideoCall() {
      this.isShowVideoCallBtnLoader = true;

      const canParticipate = await this.canUserParticipateVideoSession();

      if (!canParticipate) {
        this.notifyWarning(
          this.$t('run.collectData.notifyUserNotAllowedForVideoSession'),
        );
        this.isShowVideoCallBtnLoader = false;

        return;
      }

      const canUserCreateVideoSession = await this.canUserCreateVideoSession();

      if (canUserCreateVideoSession) {
        const sessionOwner = get(this.activeSession, 'runBy', null);
        const sessionOwnerName = get(this.activeSession, 'runByName', null);
        this.setVideoSessionMembersNames({
          ...this.videoSessionMembersNames,
          [sessionOwner]: sessionOwnerName,
        });

        await this.createVideoCall(sessionOwner, false);
      }
    }, // establishVideoCall()

    async openInvitationModal() {
      this.isShowVideoCallBtnLoader = true;
      if (await this.canUserCreateVideoSession()) {
        const isSupported = ZoomVideo.checkSystemRequirements().video && ZoomVideo.checkSystemRequirements().audio;
        if (!isSupported) {
          this.isShowVideoCallBtnLoader = false;
          return this.openBrowserNotSupportVideoCallsDialog();
        }

        return this.openComponentDialog(
          InviteUser,
          {
            title: this.$t('sessions.modals.callInvite.title'),
            subtitle: this.$t('sessions.modals.callInvite.subtitle'),
            sessionId: this.activeSession.id,
            skipUsers: [this.userDetails.userId],
          },
          async (user) => {
            if (!this) {
              // Module already destroyed
              return;
            }

            this.setVideoSessionMembersNames({
              ...this.videoSessionMembersNames,
              [user.user]: user.userName,
            });

            const isUserAlreadyInSession = !!this.sessionActivities.find(
              (activity) => activity.user === user.user && activity.status === 'in-progress',
            );

            await this.createVideoCall(user.user, !isUserAlreadyInSession);
          },
          () => {
            this.isVideoCallCreated = false;
            this.isShowVideoCallBtnLoader = false;
          },
        );
      }
      this.isShowVideoCallBtnLoader = false;
      return this.$q.dialog({
        title: this.$t('run.collectData.titleUserDoesNotHaveVideoMinutes'),
        message: this.$t('run.collectData.textUserDoesNotHaveVideoMinutes'),
        persistent: true,
      }).onOk(() => {
      });
    },

    async createVideoCall(userId, sendInvitation = false) {
      let invitedUserId = userId;

      const activeCurrentVideoSession = await this.getActiveVideoSession();

      if (activeCurrentVideoSession) {
        invitedUserId = activeCurrentVideoSession.members.find((id) => id !== activeCurrentVideoSession.runBy)
          || invitedUserId;
      }

      if (this.isVideoCallCreating || this.isVideoCallCreated || !invitedUserId) {
        return;
      }

      this.isVideoCallCreating = true;

      if (activeCurrentVideoSession) {
        this.setVideoSessionMembersNames(...activeCurrentVideoSession.members);
        this.isVideoCallCreating = false;
      } else {
        this.setVideoSessionMembersNames({
          ...this.videoSessionMembersNames,
          [this.userDetails.userId]: this.userDetails.name,
        });
      }

      this.isVideoCallCreated = true;

      this.$nextTick(async () => {
        try {
          if (this.$refs.videoContainer) {
            await this.$refs.videoContainer.showVideoCallRoom(invitedUserId);
          }

          if (this.$refs.mobileVideoContainer) {
            this.hideSessionComponent = true;
            await this.$refs.mobileVideoContainer.showVideoCallRoom(invitedUserId);
            this.isVideoMinimized = false;
          }
        } catch (err) {
          this.isVideoCallCreated = false;
          this.notifyNegative(this.$t('sessions.errorCreatingVideoSession', { error: err.message }));
        } finally {
          this.isVideoCallCreating = false;
        }
      });

      if (sendInvitation) {
        this.inviteUser(invitedUserId);
        this.openComponentDialog(
          CopyLinkDialog,
          {
            link: this.joinVideoLink,
            title: this.$t('sessions.modals.copyLink.title'),
            subtitle: this.$t('sessions.modals.copyLink.subtitle'),
          },
        );
      }
    },

    openBrowserNotSupportVideoCallsDialog() {
      this.$q.dialog({
        title: this.$t('sessions.videoSession.dialogWarnTitle'),
        message: this.$t('sessions.videoSession.textBrowserNotSupportedVideoCall'),
        cancel: true,
        persistent: false,
      });
    },

    async inviteUser(userId) {
      return callFirebaseFunction('sendCallInvite', {
        userId,
        sessionId: this.activeSession.id,
        sessionLink: this.joinVideoLink,
        clientId: this.activeSession.client,
      });
    },

    connectToVideoCall() {
      if (!this.isInvitedUser) {
        this.notifyWarning(
          this.$t('run.collectData.notifyUserNotInvitedForVideoSession'),
        );
        this.isShowVideoCallBtnLoader = false;

        return;
      }

      this.isShowVideoCallBtnLoader = true;
      this.isVideoCallCreated = true;
      sessionStorage.setItem(IS_INVITATION_ACCEPTED, true);

      this.$nextTick(async () => {
        try {
          if (this.$refs.videoContainer) {
            if (this.isVideoCallCreated && this.isInvitedUser
              && this.$refs.videoContainer.activeZoomStream === null) {
              await this.$refs.videoContainer.showVideoCallRoom(this.userDetails.userId);
            }
          }

          if (this.$refs.mobileVideoContainer && (this.isNativeAndroid || this.isNativeIos)) {
            this.hideSessionComponent = true;
            if (this.isVideoCallCreated && this.isInvitedUser
              && this.$refs.mobileVideoContainer.activeZoomStream === null) {
              await this.$refs.mobileVideoContainer.showVideoCallRoom(this.userDetails.userId);
            }
          }
        } catch (err) {
          this.isVideoCallCreated = false;
        }
      });
    }, // connectToVideoCall()

    async handleVideoCallInvitation(initialAccepted = false) {
      if (!this.isInvitedUser) {
        return;
      }

      const isInvitationAccepted = sessionStorage.getItem(IS_INVITATION_ACCEPTED);

      if (initialAccepted) {
        this.connectToVideoCall();
      } else if (!isInvitationAccepted) {
        await this.showConnectToCallDialog();
      }
    }, // handleVideoCallInvitation()

    onExpandView() {
      this.expandedView = true;
      this.saveSettings();
      this.notifyProgramsAndTargetsInvalidWorkflows();
    }, // onExpandView()

    onContractView(sessionProgram = null) {
      if (sessionProgram) {
        this.gotoSessionProgram(sessionProgram.id);
        this.notifyInvalidTargetWorkflow(sessionProgram.program);
        this.notifyInvalidActiveProgramWorkflow(sessionProgram.id);
      }
      this.expandedView = false;
      this.saveSettings();
    }, // onContractView()

    onFilterPrograms() {
      if (!isEmpty(this.filteredTags)) {
        this.filteredTags = [];
        return;
      }

      const tags = chain(this.visibleSessionPrograms)
        .reject({ pinned: true })
        .map('program.tags')
        .flatten()
        .compact()
        .uniq()
        .sort()
        .map((tag) => ({ label: tag, value: tag }))
        .value();

      this.$q.dialog({
        title: this.$t('run.collectData.filterTitle'),
        options: {
          type: 'radio',
          model: head(this.filteredTags),
          // inline: true
          items: tags,
        },
        cancel: true,
        persistent: true,
      }).onOk((data) => {
        this.filteredTags = [data];
        this.expandedView = true;
        this.saveSettings();
      });
    },

    onMinimizeVideo() {
      this.isVideoMinimized = true;
      this.saveSettings();
    }, // onMinimizeVideo()

    onRestoreVideoFromMinimized() {
      this.isVideoMinimized = false;
      this.saveSettings();
    }, // onRestoreVideoFromMinimized()

    loadSettings() {
      this.endedBySelf = false;
      const settingsJSON = localStorage.getItem('collectDataSettings');
      if (settingsJSON) {
        if (!this.isStaffTrainingSession) {
          this.expandedView = get(JSON.parse(settingsJSON), 'expandedView', false);
        }
        this.isVideoMinimized = get(JSON.parse(settingsJSON), 'isVideoMinimized', false);
      }
    }, // loadSettings()

    saveSettings() {
      const settings = { expandedView: this.expandedView, isVideoMinimized: this.isVideoMinimized };
      localStorage.setItem('collectDataSettings', JSON.stringify(settings));
    }, // saveSettings()

    hideTitle(sessionProgram) {
      return ['instructions'].includes(
        get(sessionProgram, ['program', 'type']),
      ) && (this.isStaffTrainingSession);
    },

    reloadSessionProgramWorkflows(forceRefresh = false) {
      if (forceRefresh) {
        this.resetWorkflowCacheLastFetch();
      }
      const workflowIds = chain(this.activeSessionPrograms)
        .map('program.workflow')
        .concat(
          chain(this.activeSessionPrograms)
            .flatMap('program.targets')
            .reject({ status: 'waiting' })
            .reject({ status: 'closed' })
            .map('workflow')
            .compact()
            .uniq()
            .value(),
        )
        .concat(
          chain(this.activeSessionPrograms)
            .flatMap('program.targets')
            .reject({ status: 'waiting' })
            .reject({ status: 'closed' })
            .map('defaultWorkflow')
            .compact()
            .uniq()
            .value(),
        )
        .compact()
        .uniq()
        .value();
      return this.reloadWorkflows(workflowIds);
    }, // reloadSessionProgramWorkflows()

    /**
     * Display notification at session start
     */
    notifyProgramsWithInvalidWorkflow() {
      if (isEmpty(this.filterInvalidWorkflowPrograms)) {
        return;
      }

      const programNamesWithInvalidWorkflow = chain(this.filterInvalidWorkflowPrograms)
        .map(({ programName }) => `"${programName}"`)
        .join(', ')
        .value();

      this.notifyWarning(this.$t(
        'run.collectData.errorMissingWorkflows',
        { programs: programNamesWithInvalidWorkflow },
      ));
    }, // notifyProgramsWithInvalidWorkflow()

    /**
     * Display notification when the user goes to next session program and the program has invalid workflow
     *
     * @param {string} programId - current session program id
     */
    notifyInvalidActiveProgramWorkflow(programId) {
      if (isEmpty(this.filterInvalidWorkflowPrograms) || !find(this.filterInvalidWorkflowPrograms, { id: programId })) {
        return;
      }

      this.notifyWarning(this.$t('run.collectData.invalidWorkflowSelected'));
    }, // notifyInvalidActiveProgramWorkflow()

    programTargetsHaveInvalidWorkflow(program) {
      const programTargets = get(program, 'targets');
      return some(programTargets, (target) => {
        const defaultWorkflow = chain(programTargets)
          .find({ id: target.parentTarget })
          .get('defaultWorkflow')
          .value();

        return (target.workflow && !find(this.workflows, { id: target.workflow }))
          || (defaultWorkflow && !find(this.workflows, { id: defaultWorkflow }));
      });
    }, // programTargetsHaveInvalidWorkflow()

    notifyInvalidTargetWorkflow(program) {
      if (this.programTargetsHaveInvalidWorkflow(program)) {
        this.notifyWarning(
          this.$t('run.collectData.invalidTargetWorkflowSelected', { programName: program.programName }),
        );
      }
    }, // notifyInvalidTargetWorkflow()

    /**
     * Check all the session programs and targets if they have an invalid workflow in the session's programs expand mode
     */
    notifyProgramsAndTargetsInvalidWorkflows() {
      const programsWithInvalidWorkflowsList = chain(this.activeSessionPrograms)
        .filter(({ program }) => this.programTargetsHaveInvalidWorkflow(program))
        .concat(this.filterInvalidWorkflowPrograms)
        .map(({ programName }) => `"${programName}"`)
        .join(', ')
        .value();

      if (isEmpty(programsWithInvalidWorkflowsList)) {
        return;
      }

      this.notifyWarning(
        this.$t(
          'run.collectData.invalidWorkflowsInProgramsList',
          { programsList: programsWithInvalidWorkflowsList },
        ),
      );
    }, // notifyProgramsAndTargetsInvalidWorkflows()

    async bindSessionActivities() {
      if (!get(this.activeSession, 'id')) {
        return;
      }

      const sessionActivitiesDb = new SessionActivitiesDB();
      return sessionActivitiesDb.bindAll(
        this,
        'sessionActivities',
        [
          ['clinic', '==', this.activeSession.clinic],
          ['client', '==', this.activeSession.client],
          ['session', '==', this.activeSession.id],
          ['isDeleted', '==', false],
        ],
      );
    }, // bindSessionActivities()

    async bindSessionFiles() {
      if (!isObject(this.activeSession)) {
        return;
      }

      const sessionFilesDb = new SessionFilesDB(this.activeSession.id);
      await sessionFilesDb.bindAll(this, 'sessionFiles');
    }, // bindSessionActivities()

    programsWithInvalidPromptLevels() {
      return `"${chain(this.activeSessionPrograms)
        .reject((sessionProgram) => ['instructions'].includes(get(sessionProgram, ['program', 'type'])))
        .map((sessionProgram) => (
          [
            get(sessionProgram, 'id'),

            chain(this.activePromptLevels)
              .find({ id: get(sessionProgram, ['program', 'promptLevelTemplate']) })
              .get('promptLevels')
              .size()
              .value(),
          ]))
        .fromPairs()
        .omitBy((promptLevelCount) => (promptLevelCount > 0))
        .map((promptLevelCount, sessionProgramId) => chain(this.activeSessionPrograms)
          .find({ id: sessionProgramId })
          .get(['program', 'programName'])
          .value())
        .join('", "')
        .value()
      }"`;
    }, // programsWithInvalidPromptLevels()

    /**
     * Verify that prompt levels are loaded correctly or reload them if that
     * is not the case
     */
    checkPromptLevels() {
      return chain(this.activeSessionPrograms)
        .reject((sessionProgram) => ['instructions'].includes(get(sessionProgram, ['program', 'type'])))
        .map((sessionProgram) => get(sessionProgram, ['program', 'promptLevelTemplate'], []))
        .uniq()
        .reduce((pV, promptLevelTemplateId) => {
          return pV && (chain(this.activePromptLevels)
            .find({ id: promptLevelTemplateId })
            .get('promptLevels')
            .size()
            .value() > 0);
        }, true)
        .value();
    }, // checkPromptLevels()

    programIdsWithInvalidPromptLevels() {
      return this.activeSessionPrograms.reduce((acc, sessionProgram) => {
        const programsPromptLevel = this.activePromptLevels.find(
          (plt) => plt.id === sessionProgram?.program?.promptLevelTemplate,
        );
        if (!programsPromptLevel?.promptLevels?.length) {
          acc.push(sessionProgram.id);
        }
        return acc;
      }, []);
    }, // programIdsWithInvalidPromptLevels()

    async validateAndRefreshPromptLevels() {
      await this.loadPromptLevelTemplates();
      if (!this.checkPromptLevels()) {
        // Try once more by force refreshing prompt levels
        await this.loadPromptLevelTemplates({ forceRefresh: true });
        if (!this.checkPromptLevels()) {
          this.notifyWarning(this.$t(
            'run.collectData.errorMissingPromptLevelTemplates',
            { programs: this.programsWithInvalidPromptLevels() },
          ), {
            progress: false,
            timeout: 0,
            actions: [{ label: this.$t('common.buttonOk') }],
          });
        }
      }
    }, // validateAndRefreshPromptLevels()

    bindData() {
      const dataDb = new DataDB();
      return dataDb.bindAll(
        this,
        'data',
        [['client', '==', this.activeSession.client],
          ['session', '==', this.activeSession.id]],
      );
    }, // bindData()

    unbindData() {
      const dataDb = new DataDB();
      return dataDb.unbind(this, 'data');
    }, // unbindData()

    bindDataNotes() {
      const dataNotesDb = new DataNotesDB();
      return dataNotesDb.bindAll(
        this,
        'dataNotes',
        [['client', '==', this.currentClient.id],
          ['session', '==', this.activeSession.id]],
      );
    }, // bindDataNotes()

    unbindDataNotes() {
      const dataNotesDb = new DataNotesDB();
      return dataNotesDb.unbind(this, 'dataNotes');
    }, // unbindDataNotes()

    getSessionProgramData(sessionProgram) {
      return get(this.sessionProgramsData, sessionProgram.id, {});
    }, // getSessionProgramData()

    showSessionProgramFiltered(sessionProgram) {
      return isEmpty(this.filteredTags)
        || chain(sessionProgram)
          .get('program.tags')
          .intersection(this.filteredTags)
          .size()
          .value() > 0;
    },

    showSessionProgram(sessionProgram) {
      const sessionProgramId = get(sessionProgram, 'id');
      if (isEmpty(this.filteredTags)) {
        return (this.expandedView) || (this.activeSessionProgram === sessionProgramId);
      }
      return this.showSessionProgramFiltered(sessionProgram);
    }, // showSessionProgram

    getTrial(sessionProgram) {
      return chain(this.sessionProgramsData)
        .get([sessionProgram.id, 'data'])
        .maxBy('trial')
        .get('trial', 0)
        .add(1)
        .value();
    }, // getTrial()

    /**
     * @param {string} sessionProgramId
     * @param {number} trialNumber
     */
    cacheTrialNumber(sessionProgramId, trialNumber) {
      this.currentTrial[sessionProgramId] = trialNumber;
    }, // cacheTrialNumber()

    /** @param {string} sessionProgramId */
    getCachedTrialNumber(sessionProgramId) {
      return this.currentTrial[sessionProgramId];
    }, // getCachedTrialNumber()

    refreshSortedTargets(sessionProgram) {
      const sessionProgramSelectedTargets = get(sessionProgram, 'selectedTargets', false);
      let hiddenTargets = [];
      if (!['taskAnalysis', 'sbt', 'set', 'ptp'].includes(get(sessionProgram, ['program', 'type']))) {
        hiddenTargets = get(sessionProgram, 'hiddenTargets', []);
      }

      return this.prepareTargetsForDataCollection(
        this.workflowStatuses,
        get(sessionProgram, 'program'),
        get(sessionProgram, ['program', 'targets']),
        sessionProgramSelectedTargets,
        hiddenTargets,
      );
    }, // refreshSortedTargets()

    refreshAllSortedTargets() {
      this.sortedTargets = chain(this.activeSessionPrograms)
        .keyBy('id')
        .mapValues((sessionProgram) => this.refreshSortedTargets(sessionProgram))
        .value();
    }, // refreshAllSortedTargets()

    getSortedTargets(sessionProgram) {
      return get(this.sortedTargets, sessionProgram.id, []);
    }, // getSortedTargets()

    currentPromptLevel(sessionProgram, target) {
      if (target.currentPromptLevel) {
        return find(this.targetPrompts(sessionProgram, target), { id: target.currentPromptLevel });
      }

      return last(this.targetPrompts(sessionProgram, target));
    }, // currentPromptLevel()

    targetPromptLevelTemplate(sessionProgram, target) {
      const promptLevelTemplate = target.promptLevelTemplate
        || chain(sessionProgram)
          .get(['program', 'targets'])
          .find({ id: target.parentTarget })
          .get('defaultPromptLevelTemplate')
          .value()
        || sessionProgram.program.promptLevelTemplate;

      return find(this.activePromptLevels, { id: promptLevelTemplate });
    }, // targetPromptLevelTemplate()

    targetPrompts(sessionProgram, target) {
      return get(this.targetPromptLevelTemplate(sessionProgram, target), 'promptLevels');
    }, // targetPrompts()

    maxTargetPromptOrder(sessionProgram, target) {
      return chain(this.targetPrompts(sessionProgram, target))
        .reject({ excludeFromPromptLevelFading: true })
        .last()
        .get('order')
        .value();
    }, // maxTargetPromptOrder()

    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (isLoading) {
        this.$q.loading.show();
      } else {
        this.$q.loading.hide();
      }
    }, // setLoading()

    async closeVideoChat() {
      this.moveToSessionScreen();
      this.isVideoCallCreated = false;
      this.isShowVideoCallBtnLoader = false;
      await this.syncCurrentClient();
    }, // closeVideoChat()

    moveToSessionScreen() {
      this.hideSessionComponent = false;
      this.isShowReturnToVideoCallBtn = true;
      if (this.$refs.mobileVideoContainer) {
        this.$refs.mobileVideoContainer.$el.classList.add('hide');
      }
    }, // moveToSessionScreen()

    moveToVideoCallScreen() {
      this.hideSessionComponent = true;
      this.isShowReturnToVideoCallBtn = false;
      if (this.$refs.mobileVideoContainer) {
        this.$refs.mobileVideoContainer.$el.classList.remove('hide');
      }
    }, // moveToVideoCallScreen()

    /**
     * Save geolocation data to a session activity
     * @param {*} field Field to save to
     * @param {*} sessionActivityId Session activity id
     * @returns
     */
    async saveSessionActivityGeolocation(field, sessionActivityId) {
      if (!['startGeolocation', 'endGeolocation'].includes(field) || !sessionActivityId) {
        return;
      }

      return new Promise((resolve, rejectCall) => {
        this.executeGeolocationCallback(async (geolocation) => {
          try {
            if (isEmpty(geolocation) && this.evvRequired) {
              this.openComponentDialog(
                EVVDialog,
                {
                  persistent: true,
                },
                async (evvResult) => {
                  if (evvResult?.retry) {
                    await this.refreshGeolocation(true);
                    this.saveSessionActivityGeolocation(field, sessionActivityId);
                  } else {
                    const { evvInfo } = evvResult;
                    const sessionActivitiesDb = new SessionActivitiesDB();
                    await sessionActivitiesDb.set({ [field]: { ...evvInfo } }, sessionActivityId);
                  }
                  resolve();
                },
              );
            } else {
              const sessionActivitiesDb = new SessionActivitiesDB();
              await sessionActivitiesDb.update({ [field]: geolocation, id: sessionActivityId });
              resolve();
            }
          } catch (error) {
            rejectCall(error);
          }
        });
      });
    }, // saveSessionActivityGeolocation()

  },
};
